/**
 * Parse / validate GenUI `{ type: "bento", cells: [...] }` documents.
 * Callers: parse-genui tryParseGenuiSpec*, InsightsGenuiBento, fixtures/tests.
 * No existing twin — Grep/Glob found no parse-genui-bento*.
 */

import {
  type GenuiActionsPayload,
  type GenuiBarPayload,
  type GenuiCaveatPayload,
  type GenuiCellPayload,
  type GenuiColSpan,
  type GenuiEstatePayload,
  type GenuiHeaderPayload,
  type GenuiKpiPayload,
  type GenuiMetaPayload,
  type GenuiTablePayload,
  type GenuiTextPayload,
  type GenuiWidgetKind,
  type InsightsGenuiBento,
  type InsightsGenuiBentoCell,
  isGenuiColSpan,
  isGenuiWidgetKind,
} from "./genui-bento-types";

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function parseHeaderPayload(raw: unknown): GenuiHeaderPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const title = typeof r.title === "string" ? r.title.trim() : "";
  if (!title) return null;
  return {
    title,
    subtitle: typeof r.subtitle === "string" ? r.subtitle : undefined,
    frame: typeof r.frame === "string" ? r.frame : undefined,
  };
}

function parseKpiPayload(raw: unknown): GenuiKpiPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const label = typeof r.label === "string" ? r.label.trim() : "";
  const value =
    typeof r.value === "string" ? r.value.trim() : r.value != null ? String(r.value) : "";
  if (!label || !value) return null;
  return {
    label,
    value,
    hint: typeof r.hint === "string" ? r.hint : undefined,
  };
}

function parseActionsPayload(raw: unknown): GenuiActionsPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.items)) return null;
  const items = r.items
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (
        item &&
        typeof item === "object" &&
        typeof (item as { text?: unknown }).text === "string"
      ) {
        return (item as { text: string }).text.trim();
      }
      return "";
    })
    .filter(Boolean)
    .slice(0, 3);
  if (!items.length) return null;
  return { items };
}

function parseEstatePayload(raw: unknown): GenuiEstatePayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.items)) return null;
  const items = r.items
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rec = item as Record<string, unknown>;
      const id = typeof rec.id === "string" ? rec.id.trim() : "";
      const label = typeof rec.label === "string" ? rec.label.trim() : "";
      const status = typeof rec.status === "string" ? rec.status.trim() : "";
      if (!id || !label || !status) return null;
      return { id, label, status };
    })
    .filter((x): x is { id: string; label: string; status: string } => Boolean(x));
  if (!items.length) return null;
  return { items };
}

function parseCaveatPayload(raw: unknown): GenuiCaveatPayload | null {
  if (typeof raw === "string" && raw.trim()) return { text: raw.trim() };
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const text = typeof r.text === "string" ? r.text.trim() : "";
  if (!text) return null;
  return { text };
}

function parseMetaPayload(raw: unknown): GenuiMetaPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const out: GenuiMetaPayload = {};
  if (typeof r.asOf === "string") out.asOf = r.asOf;
  if (typeof r.investigationId === "string") out.investigationId = r.investigationId;
  if (typeof r.frame === "string") out.frame = r.frame;
  return out;
}

function parseTextPayload(raw: unknown): GenuiTextPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const markdown =
    typeof r.markdown === "string" ? r.markdown : typeof r.text === "string" ? r.text : "";
  if (!markdown.trim()) return null;
  return { markdown };
}

function parseTablePayload(raw: unknown): GenuiTablePayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.columns) || !Array.isArray(r.rows)) return null;
  return {
    title: typeof r.title === "string" ? r.title : undefined,
    columns: r.columns.map((c) => String(c)),
    rows: r.rows.map((row) =>
      Array.isArray(row) ? row.map((cell) => (cell == null ? "" : cell)) : [],
    ),
  };
}

function parseBarPayload(raw: unknown): GenuiBarPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const x = typeof r.x === "string" ? r.x.trim() : "";
  const y = typeof r.y === "string" ? r.y.trim() : "";
  if (!x || !y || !Array.isArray(r.data)) return null;
  const data: Array<{ label: string; value: number }> = [];
  for (const row of r.data) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const value = asNumber(record[y] ?? record.value);
    if (value == null) continue;
    const rawLabel = record[x] ?? record.label;
    const label = rawLabel == null || rawLabel === "" ? "—" : String(rawLabel);
    data.push({ label, value });
  }
  if (!data.length) return null;
  return {
    title: typeof r.title === "string" ? r.title : undefined,
    x,
    y,
    data,
  };
}

function parsePayload(kind: GenuiWidgetKind, raw: unknown): GenuiCellPayload | null {
  switch (kind) {
    case "header":
      return parseHeaderPayload(raw);
    case "kpi":
      return parseKpiPayload(raw);
    case "actions":
      return parseActionsPayload(raw);
    case "estate":
      return parseEstatePayload(raw);
    case "caveat":
      return parseCaveatPayload(raw);
    case "meta":
      return parseMetaPayload(raw);
    case "text":
      return parseTextPayload(raw);
    case "table":
      return parseTablePayload(raw);
    case "bar":
      return parseBarPayload(raw);
    default:
      return null;
  }
}

function parseCell(raw: unknown, index: number): InsightsGenuiBentoCell | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!isGenuiWidgetKind(r.kind)) return null;
  if (!isGenuiColSpan(r.colSpan)) return null;
  const payload = parsePayload(r.kind, r.payload);
  if (!payload) return null;
  const id = typeof r.id === "string" && r.id.trim() ? r.id.trim() : `${r.kind}-${index}`;
  const iconRaw = typeof r.icon === "string" ? r.icon.trim().toLowerCase() : "";
  const icon = iconRaw || undefined;
  return { id, kind: r.kind, colSpan: r.colSpan as GenuiColSpan, icon, payload };
}

/**
 * Strict parse of a complete bento document.
 * Rejects unknown kinds and invalid colSpans (no silent rewrite).
 */
export function parseBentoSpec(parsed: Record<string, unknown>): InsightsGenuiBento | null {
  if (parsed.type !== "bento") return null;
  if (!Array.isArray(parsed.cells)) return null;
  const cells: InsightsGenuiBentoCell[] = [];
  for (let i = 0; i < parsed.cells.length; i += 1) {
    const cell = parseCell(parsed.cells[i], i);
    if (!cell) return null;
    cells.push(cell);
  }
  if (!cells.length) return null;
  return { type: "bento", cols: 12, cells };
}

/**
 * Streaming: keep only fully-valid cells; stop at first incomplete.
 * Returns a shell once type is bento even with zero cells.
 */
export function parseBentoSpecStreaming(
  parsedOrPartial: Record<string, unknown>,
): InsightsGenuiBento | null {
  if (parsedOrPartial.type !== "bento") return null;
  const cellsRaw = parsedOrPartial.cells;
  const cells: InsightsGenuiBentoCell[] = [];
  if (Array.isArray(cellsRaw)) {
    for (let i = 0; i < cellsRaw.length; i += 1) {
      const cell = parseCell(cellsRaw[i], i);
      if (cell) cells.push(cell);
      else break;
    }
  }
  return { type: "bento", cols: 12, cells };
}

/** True when bento has ≥1 data widget (kpi|table|bar). */
export function bentoHasDataWidget(bento: InsightsGenuiBento): boolean {
  return bento.cells.some((c) => c.kind === "kpi" || c.kind === "table" || c.kind === "bar");
}
